import React, { useState, useEffect, useRef, useMemo } from 'react';
import './index.scss';
import { Button } from 'tea-component';
import TinyEditor from '@sg/components/TinyEditor';
import CustomCollapse from '@sg/components/Collapse';
import { STORAGE_KEYS, sessionStorageUtils } from '@sg/utils/sessionStorage';
import { useIntl } from "umi";
import { SGBuriedBtnPoint, createBtnPoint, ExecutiveSummaryTab } from '@sg/utils/buriedPoint';
import IconTips from '@sg/components/IconTips';
import { useAutoSaveParams } from '@sg/hooks/useAutoSaveParams';
import AutoSaveModal from '@/pages/strategic/components/AutoSaveModal';
import RoutePrompt from '@/pages/strategic/components/RoutePrompt';
import { renderRichText } from '@sg/utils/commonPageMethod';

interface RichTextProps {
  data: string; // HTML string to render as rich text
  collapseProps?: any; // Pass-through props for Collapse
  editable?: boolean; // Whether the content is editable
  onSave?: (newData: string) => void; // Callback when content is saved
  userPermissions?: {
    canEdit?: boolean;
  };
  collapsed?: boolean; // Control collapse state externally
  onToggle?: () => void; // Callback when collapse state changes
  onHeightChange?: (height: number) => void;
  syncedHeight?: number;
  notesType?: 'revenue' | 'profit'; // Type identifier for buried point tracking
  tipString?: string;
  subtitle?: string; // Subtitle text to display beside IconTips (e.g., "Updated on 2025-03-04")
  // Auto-save related props
  autoSavePageKey?: string; // Unique identifier for auto-save (e.g., from EDIT_PAGE_KEY_MAP)
  autoSaveId?: string | number; // Additional identifier (e.g., project_id, studio_id)
}

const RichText: React.FC<RichTextProps> = ({
  data,
  collapseProps,
  editable = false,
  onSave,
  userPermissions = { canEdit: false },
  collapsed = true,
  onToggle,
  onHeightChange,
  syncedHeight,
  notesType,
  tipString,
  subtitle,
  autoSavePageKey,
  autoSaveId,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data);
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const { formatMessage } = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef<number>(0);
  const pendingFrameRef = useRef<number | null>(null);

  // Auto-save: construct unique pageKey
  const pageKeyCur = useMemo(() => {
    if (!autoSavePageKey) return '';
    return autoSaveId !== undefined
      ? `${autoSavePageKey}_${autoSaveId}`
      : autoSavePageKey;
  }, [autoSavePageKey, autoSaveId]);

  // Auto-save: get methods (check if auto-save exists)
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
    pageKey: pageKeyCur,
  });

  // Auto-save: start auto-saving when in edit mode
  useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams: { note: data },
    saveParamsCur: { note: editData },
    isBegin: isEditing && !!autoSavePageKey,
    isChangeValue: true,
  });

  // When using external control, sync with storage on mount
  useEffect(() => {
    if (onToggle && collapsed !== undefined) {
      // Load from storage and sync with external state
      const savedState = sessionStorageUtils.get(STORAGE_KEYS.RICH_TEXT_COLLAPSE_STATE, []);
      const isCurrentlyCollapsed = savedState.length === 0;

      // If storage state differs from external state, update external state
      if (isCurrentlyCollapsed !== collapsed) {
        onToggle();
      }
    }
  }, [onToggle, collapsed]);

  // Sync internal state with external state when using external control
  useEffect(() => {
    if (onToggle && collapsed !== undefined) {
      const newActiveIds = collapsed ? [] : ['1'];
      if (JSON.stringify(activeIds) !== JSON.stringify(newActiveIds)) {
        setActiveIds(newActiveIds);
      }
    }
  }, [collapsed, onToggle, activeIds, setActiveIds]);

  // Sync editData with data prop when not editing
  useEffect(() => {
    if (!isEditing) {
      setEditData(data);
    }
  }, [data, isEditing]);

  const handleEdit = (source: 'button' | 'link' = 'button') => {
    // Check for auto-save restore prompt if auto-save is enabled
    if (autoSavePageKey && pageKeyCur && showRestorePrompt(pageKeyCur)) {
      setAutoSaveVisible(true);
      return;
    }

    // Track buried point for Notes Edit button (only on Executive Summary page)
    // Only track if notesType is provided (revenue or profit)
    if (notesType && (notesType === 'revenue' || notesType === 'profit')) {
      const buttonKey = notesType === 'revenue' ? 'revenueNotesEdit' : 'profitNotesEdit';
      const sourceText = source === 'link' ? 'Add Content Link' : 'Edit Button';
      const btnPoint = createBtnPoint(
        ExecutiveSummaryTab,
        buttonKey,
        `${notesType === 'revenue' ? 'Revenue' : 'Profit'} Notes Edit - ${sourceText}`
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    }

    setEditData(data);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(editData);
    }
    // Clear auto-save data after successful save
    if (autoSavePageKey && pageKeyCur) {
      deleteSaveParamsByPageKey(pageKeyCur);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data);
    // Clear auto-save data when canceling
    if (autoSavePageKey && pageKeyCur) {
      deleteSaveParamsByPageKey(pageKeyCur);
    }
    setIsEditing(false);
  };

  // Auto-save restore handlers
  const handleAutoSaveConfirm = () => {
    const savedData = getSaveParamsValue(pageKeyCur);
    if (savedData?.note !== undefined) {
      setEditData(savedData.note);
      setIsEditing(true);
    }
    setAutoSaveVisible(false);
  };

  const handleAutoSaveCancel = () => {
    // Clear auto-save data when user chooses not to restore
    if (pageKeyCur) {
      deleteSaveParamsByPageKey(pageKeyCur);
    }
    setAutoSaveVisible(false);
  };

  const canEdit = editable && userPermissions.canEdit;

  useEffect(() => {
    if (!onHeightChange || !containerRef.current) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const height = entry.contentRect.height;
      if (height > 2000 || height < 0) {
        return;
      }
      if (Math.abs(height - lastHeightRef.current) > 1) {
        const nextHeight = height;
        lastHeightRef.current = nextHeight;
        if (pendingFrameRef.current) {
          cancelAnimationFrame(pendingFrameRef.current);
        }
        pendingFrameRef.current = requestAnimationFrame(() => {
          pendingFrameRef.current = null;
          onHeightChange(nextHeight);
        });
      }
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      if (pendingFrameRef.current) {
        cancelAnimationFrame(pendingFrameRef.current);
        pendingFrameRef.current = null;
      }
    };
  }, [onHeightChange]);

  return (
    <>
      <div ref={containerRef} className="dbs-rich-text-container">
        {/* onToggle here is used as a flag to determine whether the collapse state is controlled externally or internally */}
        <CustomCollapse
          storageKey={STORAGE_KEYS.RICH_TEXT_COLLAPSE_STATE}
          activeIds={activeIds} // Use internal state, sync with external state via useEffect
          setActiveIds={(newIds: string[]) => {
            const previousActiveIds = activeIds;
            setActiveIds(newIds);

            // Track buried point for Notes collapse (only on Executive Summary page)
            // Only track if notesType is provided (revenue or profit)
            if (notesType && (notesType === 'revenue' || notesType === 'profit')) {
              const isExpanded = newIds.includes('1');
              const wasExpanded = previousActiveIds.includes('1');

              // Only track when state actually changes (expanded to collapsed or vice versa)
              if (isExpanded !== wasExpanded) {
                const buttonKey = notesType === 'revenue' ? 'revenueNotesCollapse' : 'profitNotesCollapse';
                const btnPoint = createBtnPoint(
                  ExecutiveSummaryTab,
                  buttonKey,
                  `${notesType === 'revenue' ? 'Revenue' : 'Profit'} Notes - ${isExpanded ? 'Expanded' : 'Collapsed'}`
                );
                if (btnPoint) {
                  SGBuriedBtnPoint(btnPoint);
                }
              }
            }

            // If using external control, also call the external toggle
            if (onToggle) {
              onToggle();
            }
          }}
          className="dbs-rich-text-collapse"
          title={
            <div className="dbs-rich-text-header">
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <div>{formatMessage({ id: 'dbs_notes' })}</div>
                {tipString && <IconTips tipString={tipString} />}
                {subtitle && <div className="dbs-rich-text-subtitle">{subtitle}</div>}
              </div>
              {canEdit && !isEditing && (
                <Button
                  onClick={(e) => {
                    e?.stopPropagation(); // Prevent collapse event
                    // If collapse is closed, open it first
                    if (activeIds.length === 0) {
                      const newActiveIds = ['1'];
                      setActiveIds(newActiveIds);
                      // Update session storage
                      sessionStorageUtils.set(STORAGE_KEYS.RICH_TEXT_COLLAPSE_STATE, newActiveIds);
                      // If using external control, also call the external toggle
                      if (onToggle) {
                        onToggle();
                      }
                    }
                    handleEdit();
                  }}
                  style={{ height: '30px', minWidth: '60px' }}
                  className="dbs-gradient-btn"
                >
                  {formatMessage({ id: 'dbs_edit' })}
                </Button>
              )}
              {canEdit && isEditing && (
                <div className="dbs-rich-text-buttons">
                  <Button
                    onClick={(e) => {
                      e?.stopPropagation(); // Prevent collapse event
                      handleSave();
                    }}
                    style={{ height: '30px', minWidth: '76px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_confirm' })}
                  </Button>
                  <Button
                    onClick={(e) => {
                      e?.stopPropagation(); // Prevent collapse event
                      handleCancel();
                    }}
                    style={{ height: '30px', minWidth: '76px' }}
                    className="dbs-outline-btn"
                  >
                    {formatMessage({ id: 'dbs_cancel' })}
                  </Button>
                </div>
              )}
            </div>
          }
          panelId="1"
          {...collapseProps}
        >
          <div
            className="dbs-rich-text-body"
            style={syncedHeight !== undefined ? { minHeight: `${syncedHeight}px` } : undefined}
          >
            {isEditing ? (
              <div className="dbs-rich-text-editor">
                <TinyEditor
                  value={editData}
                  getEditorContent={(content) => setEditData(content)}
                  isShowToolbar={true}
                  minHeight={350}
                  maxContentHeight={350}
                  isMaxHeightScroll={true}
                  placeholder={formatMessage({ id: 'dbs_please_enter' })}
                />
              </div>
            ) : (
              <div className="dbs-rich-text-content">
                {!data || data.trim() === '' || data === '<p></p>' || data === '<br>' ? (
                  <div
                    className="dbs-rich-text-content-inner"
                    style={syncedHeight !== undefined ? { minHeight: `${syncedHeight}px` } : undefined}
                  >
                    <div className="dbs-rich-text-no-data">
                      {canEdit ? (
                        <div>
                          No Data. Please
                          <span onClick={() => handleEdit('link')} style={{ cursor: 'pointer', color: '#7F6FF1', marginLeft: '4px' }}>Add Content</span>
                        </div>
                      ) : (
                        <div>No Data</div>
                      )}
                    </div>
                  </div>
                ) : (
                  // <div className="dbs-rich-text-html" dangerouslySetInnerHTML={{ __html: data }} />
                  renderRichText(data, 'dbs-rich-text-html')
                )}
              </div>
            )}
          </div>
        </CustomCollapse>
      </div>
      {/* Auto-save recovery modal */}
      {autoSavePageKey && (
        <AutoSaveModal
          visible={autoSaveVisible}
          onConfirm={handleAutoSaveConfirm}
          onClose={handleAutoSaveCancel}
        />
      )}

      {/* RoutePrompt to intercept navigation when there are unsaved changes */}
      <RoutePrompt
        when={isEditing && editData !== data}
        onOk={async () => {
          // Save changes before navigating away
          await handleSave();
          // Navigation will proceed after save completes
        }}
      />
    </>
  );
};

export default RichText;
